import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { analytics } from "@/lib/analytics";

async function readSseStream(resp, onDelta, onDone) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let acc = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const payload = JSON.parse(line.slice(5).trim());
      if (payload.delta) {
        acc += payload.delta;
        onDelta(acc);
      } else if (payload.error) {
        toast(payload.error);
      } else if (payload.done) {
        onDone(payload.message);
      }
    }
  }
}

export function useChatSession(id) {
  const navigate = useNavigate();
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [ending, setEnding] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    api
      .get(`/conversations/${id}`)
      .then(({ data }) => {
        setConvo(data);
        if (!hydratedRef.current) {
          setMessages(data.messages || []);
          hydratedRef.current = true;
        }
      })
      .catch(() => {
        toast("This reflection could not be found.");
        navigate("/begin");
      });
  }, [id, navigate]);

  const sendMessage = async (content) => {
    if (!content || streaming) return;
    hydratedRef.current = true;
    const messageIndex = messages.length;
    analytics.messageSent({ conversationId: id, messageIndex });
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content },
    ]);
    setStreaming(true);
    setStreamText("");
    try {
      const resp = await fetch(`${API}/conversations/${id}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      await readSseStream(
        resp,
        (acc) => setStreamText(acc),
        (msg) => {
          setMessages((prev) => [...prev, msg]);
          setStreamText("");
        }
      );
    } catch (_) {
      toast("Mirror is quiet right now. Please try again.");
    } finally {
      setStreaming(false);
      setStreamText("");
    }
  };

  const endSession = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await api.post(`/conversations/${id}/end`);
      analytics.conversationEnded({ messageCount: messages.length });
      toast("This reflection has been saved to your Journey.", {
        description: "You can reopen this conversation anytime.",
      });
      navigate("/journey", { state: { justEnded: true } });
    } catch (err) {
      toast(err.response?.data?.detail || "Could not close this reflection.");
      setEnding(false);
    }
  };

  const renameConversation = async (title) => {
    const clean = (title || "").trim();
    if (!clean) return;
    try {
      await api.patch(`/conversations/${id}`, { title: clean });
      setConvo((c) => (c ? { ...c, title: clean } : c));
    } catch (err) {
      toast(err.response?.data?.detail || "Could not rename this conversation.");
    }
  };

  return { convo, messages, streaming, streamText, ending, sendMessage, endSession, renameConversation };
}
